/**
 * What a building looks like once it has been hit, and what it leaves behind.
 *
 * Damage used to be a tint: the same house, reddened. That reads at a glance as
 * "this one is hurt" and not at all as "this one is coming down", which is the
 * difference the brief was asking about. The generator bakes a **damaged** and
 * a **destroyed** still for every building shape, plus a **rubble** pile, so
 * the three states are three pictures.
 *
 * ── Keyed by shape, not by building id ─────────────────────────────────────
 *
 * There are twenty-five `BuildingShape`s and rather more buildings, and a
 * broken granary and a broken storehouse are the same broken. Keying on the
 * shape is what makes seventy-five stills cover the whole catalogue, and it is
 * the same key `render/textures.ts` already bakes the intact vector from.
 *
 * ── Not namespaced by vernacular, and that is a known compromise ────────────
 *
 * `buildingArt` looks intact stills up under `art/buildings/<vernacular>/`,
 * because a Litany hall and an Ashen hall are different buildings. These are
 * not: one set of wrecks serves every people. A bone-built Litany hall
 * therefore comes down into an ash-and-timber ruin, which is wrong and is
 * accepted for now — the alternative is eleven times the art for a state each
 * building is in for about ninety seconds. Written down rather than hidden; if
 * the wrecks are ever re-baked per vernacular, this is the one function to
 * change.
 */

/**
 * Below this fraction of health a building wears its damaged still, and below
 * the second it wears its wreck.
 *
 * 0.6 is where `drawStructures` already starts a building smoking, so the two
 * cues arrive together and a roof that has caught is a roof that looks it.
 * 0.25 is late enough that "destroyed" is a warning rather than a description —
 * a player who sees a wreck still standing has a few seconds to act, which is
 * the whole point of showing it.
 */
export const WRECK_DAMAGED = 0.6;
export const WRECK_DESTROYED = 0.25;

/**
 * The still for a building of this shape at this fraction of health, or null if
 * it is healthy enough to be drawn as itself.
 */
export function wreckArt(shape: string, hpFraction: number): string | null {
  if (hpFraction >= WRECK_DAMAGED) return null;
  return `wreck_${shape}_${hpFraction < WRECK_DESTROYED ? 'destroyed' : 'damaged'}`;
}

/** The pile a building of this shape leaves on the ground. */
export function rubbleArt(shape: string): string {
  return `rubble_${shape}`;
}
